import React from 'react'
import { Box, Button, Stack, TextField } from '@mui/material'

import { IClientInvoiceInput } from '../../../Services/clientInvoices'

interface Props {
	busy: boolean
	onSubmit: (data: IClientInvoiceInput) => Promise<unknown>
	onError: (message: string) => void
}

const today = () => new Date().toISOString().slice(0, 10)

/**
 * Saisie d'une facture : numéro, date d'émission, montant HT. Rien d'autre.
 *
 * ⚠️ Pas de case « déjà payée » : une facture naît impayée et se marque payée ensuite. Les
 * deux gestes n'engagent pas la même chose — le second émet un event et crée la commission
 * d'un partenaire. Les réunir en une case à cocher ferait déclencher ça par inadvertance.
 *
 * ⚠️ Pas de montant TTC : la commission porte sur le HT, et un champ qu'on ressaisit sans
 * que rien ne le vérifie finit par être faux.
 *
 * ⚠️ Le montant se saisit en EUROS et part en CENTIMES. La conversion passe par
 * `Math.round` : `12.34 * 100` vaut 1233.9999999999998 en JavaScript, et une troncature
 * ferait perdre un centime sur une facture sur deux.
 */
export const ClientInvoiceForm: React.FC<Props> = ({ busy, onSubmit, onError }) => {
	const [number, setNumber] = React.useState('')
	const [issuedAt, setIssuedAt] = React.useState(today)
	const [excl, setExcl] = React.useState('')

	const canSubmit = Boolean(number.trim()) && Boolean(issuedAt) && Boolean(excl) && !busy

	const submit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!canSubmit) return
		const amountExcludingTaxCents = Math.round(parseFloat(excl.replace(',', '.')) * 100)
		if (!Number.isFinite(amountExcludingTaxCents)) {
			onError('Montant HT invalide')
			return
		}
		try {
			await onSubmit({ number: number.trim(), issuedAt, amountExcludingTaxCents })
			setNumber('')
			setExcl('')
		} catch (err: any) {
			onError(err?.message || 'Création impossible')
		}
	}

	return (
		<Box component="form" onSubmit={submit} sx={{ mt: 2 }}>
			<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
				<TextField
					size="small"
					label="N° de facture"
					value={number}
					onChange={(e) => setNumber(e.target.value)}
					sx={{ width: 180 }}
					required
				/>
				<TextField
					size="small"
					label="Date d'émission"
					type="date"
					value={issuedAt}
					onChange={(e) => setIssuedAt(e.target.value)}
					InputLabelProps={{ shrink: true }}
					sx={{ width: 180 }}
					required
				/>
				<TextField
					size="small"
					label="Montant HT (€)"
					value={excl}
					onChange={(e) => setExcl(e.target.value)}
					sx={{ width: 160 }}
					required
				/>
				<Button type="submit" variant="contained" size="small" disabled={!canSubmit}>
					Ajouter
				</Button>
			</Stack>
		</Box>
	)
}
